<!--
  TimeRowEditor — one line of a list of times, open for editing.

  The line itself becomes the typed box ("sep 14 1-2:30pm"); expanded, the
  structured inputs sit under it, and both edit the same row. The add line is
  the same editor on a new row, empty until something is typed or picked. The
  kind picker shows only where the field leaves the kind open (the aggregate
  Times field); each kind brings its own inputs — hours pick weekdays, a slot
  its seats, a lifetime can be ongoing. The zone shows only for a timed row.

  Emits `update` with the whole row (snake_case, UTC instants) on every change,
  `enter` when Enter is pressed in the typed box, and `toggle` on a click on the
  line outside its input and buttons — the way to open or close a line whose
  time isn't typed (hours, slots, lifetimes). Slots: `icon` leads the line (the
  kind's icon by default), `end` closes it (badges, ×).
-->
<template>
  <div class="time-row-editor">
    <div class="head" @click="onHeadClick">
      <slot name="icon">
        <font-awesome-icon :icon="kindIcon" class="kind-icon" fixed-width />
      </slot>
      <input
        v-if="typable"
        ref="quickRef"
        class="typed"
        :class="{ 'is-invalid': !!typedError }"
        :value="typed"
        :placeholder="placeholder"
        aria-label="When"
        @input="onTyped(($event.target as HTMLInputElement).value)"
        @keydown.enter.prevent="emit('enter')"
      />
      <span v-else class="typed is-static">{{ summaryText }}</span>
      <slot name="end" />
    </div>

    <div v-if="expanded" class="fields">
      <span v-if="hint" class="hint" :class="{ 'is-error': !!typedError }">{{ hint }}</span>

      <div v-if="kindEditable" class="kinds" role="group" aria-label="Kind">
        <button
          v-for="k in TIME_KINDS"
          :key="k.key"
          type="button"
          class="kind"
          :class="{ 'is-selected': k.key === parts.kind }"
          :aria-pressed="k.key === parts.kind"
          :title="k.note"
          @click="setKind(k.key)"
        >
          <font-awesome-icon :icon="k.icon" fixed-width />
          <span>{{ k.label }}</span>
        </button>
      </div>

      <div v-if="parts.kind === 'hours'" class="inputs" role="group" aria-label="Days">
        <button
          v-for="code in WEEKDAY_CODES"
          :key="code"
          type="button"
          class="day btn btn-sm"
          :class="{ 'is-selected': weekdays.includes(code) }"
          :aria-pressed="weekdays.includes(code)"
          @click="toggleDay(code)"
        >
          {{ weekdayLabel(code).slice(0, 2) }}
        </button>
      </div>

      <div class="inputs">
        <label v-if="parts.kind === 'instant' || parts.kind === 'event'" class="check">
          <input type="checkbox" :checked="parts.allDay" @change="toggleAllDay" />
          All day
        </label>
        <label v-if="parts.kind === 'lifetime'" class="check">
          <input type="checkbox" :checked="!parts.endDate" @change="toggleOngoing" />
          Ongoing
        </label>
        <label v-if="parts.kind === 'slot'" class="check">
          Seats
          <input
            class="input is-small seats"
            type="number"
            min="1"
            :value="row.capacity ?? ''"
            @change="setCapacity(($event.target as HTMLInputElement).value)"
          />
        </label>
      </div>

      <div class="inputs">
        <input
          v-if="parts.kind !== 'hours'"
          class="input is-small date"
          type="date"
          :value="parts.date"
          aria-label="Start date"
          @change="setStartDate(($event.target as HTMLInputElement).value)"
        />
        <input
          v-if="timed"
          class="input is-small clock"
          type="time"
          :value="parts.time ?? ''"
          aria-label="Start time"
          @change="patchParts({ time: ($event.target as HTMLInputElement).value || null })"
        />
        <template v-if="hasEnd">
          <span class="to">to</span>
          <input
            v-if="showEndDate"
            class="input is-small date"
            type="date"
            :value="parts.endDate ?? parts.date"
            aria-label="End date"
            @change="setEndDate(($event.target as HTMLInputElement).value)"
          />
          <input
            v-if="timed"
            class="input is-small clock"
            type="time"
            :value="parts.endTime ?? ''"
            aria-label="End time"
            @change="patchParts({ endTime: ($event.target as HTMLInputElement).value || null })"
          />
        </template>
      </div>

      <div class="inputs">
        <select
          v-if="timed"
          class="input is-small zone"
          :value="parts.tz"
          aria-label="Time zone"
          @change="setZone(($event.target as HTMLSelectElement).value)"
        >
          <option v-for="tz in zones" :key="tz" :value="tz">{{ zoneName(tz) }}</option>
        </select>
        <input
          class="input is-small title"
          :value="row.title ?? ''"
          placeholder="Title (optional)"
          aria-label="Title"
          @input="emitRow({ title: ($event.target as HTMLInputElement).value || null })"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue"
import { getBrowserTimeZone } from "@auger/ui/utils/date"
import {
  addDays,
  daysBetween,
  formatTime,
  isParsed,
  longDay,
  parseWhen,
  rruleWeekdays,
  storedInstants,
  TIME_KINDS,
  type TimeKind,
  type TimeParts,
  timeParts,
  type TimeRow,
  typedForm,
  WEEKDAY_CODES,
  weekdayLabel,
  weeklyRrule,
  zoneAbbreviation,
} from "@auger/ui/utils/times"

const props = withDefaults(
  defineProps<{
    row: TimeRow
    /** Whether the kind can change: only where the field leaves it open. */
    kindEditable?: boolean
    /** Show the structured inputs under the line. */
    expanded?: boolean
    /** A new row: the line starts empty until something is typed or picked. */
    adding?: boolean
    placeholder?: string
    autofocus?: boolean
    /** Typed text to start with (a key pressed on the cell). */
    seed?: string
  }>(),
  {
    kindEditable: false,
    expanded: true,
    adding: false,
    placeholder: "",
    autofocus: true,
    seed: "",
  }
)

const emit = defineEmits<{
  update: [row: TimeRow]
  enter: []
  toggle: []
}>()

const parts = computed<TimeParts>(
  () =>
    timeParts(props.row) ?? {
      kind: (props.row.time_type as TimeKind) ?? "event",
      date: "",
      time: null,
      endDate: null,
      endTime: null,
      allDay: true,
      tz: getBrowserTimeZone(),
    }
)
const timed = computed(() => !parts.value.allDay && parts.value.kind !== "lifetime")
const hasEnd = computed(
  () => parts.value.kind !== "instant" && !(parts.value.kind === "lifetime" && !parts.value.endDate)
)
const showEndDate = computed(
  () =>
    parts.value.kind === "lifetime" ||
    (parts.value.kind === "event" &&
      (parts.value.allDay || (!!parts.value.endDate && parts.value.endDate !== parts.value.date)))
)
const weekdays = computed(() => rruleWeekdays(props.row.rrule))

// --- Writing the row ------------------------------------------------------------

// A new row's line stays empty until it has been given a time.
const given = ref(!props.adding)

function emitRow(changes: Partial<TimeRow>) {
  given.value = true
  emit("update", { ...props.row, ...changes })
}

/** Store new wall-clock parts as the row's UTC instants. */
function patchParts(changes: Partial<TimeParts>, extra: Partial<TimeRow> = {}) {
  const next = { ...parts.value, ...changes }
  typedText.value = null
  emitRow({
    ...storedInstants(next, next.tz),
    time_type: next.kind,
    timezone: next.tz,
    ...extra,
  })
}

function setStartDate(date: string) {
  if (!date) return
  const p = parts.value
  // Moving the start moves the end with it: a span keeps its length.
  const length = p.endDate ? daysBetween(p.date, p.endDate) : 0
  patchParts({ date, endDate: p.endDate ? addDays(date, length) : p.endDate })
}

function setEndDate(date: string) {
  if (!date) return
  patchParts({ endDate: date < parts.value.date ? parts.value.date : date })
}

function setZone(tz: string) {
  // The wall clock stays put; the instant moves with the zone.
  patchParts({ tz })
}

function toggleAllDay() {
  const p = parts.value
  if (p.allDay) {
    patchParts({
      allDay: false,
      time: "09:00",
      endTime: p.kind === "instant" ? null : "10:00",
      endDate: p.kind === "instant" ? null : p.date,
    })
  } else {
    patchParts({ allDay: true, time: null, endTime: null })
  }
}

function toggleOngoing() {
  const p = parts.value
  patchParts({ endDate: p.endDate ? null : addDays(p.date, 365) })
}

function setCapacity(value: string) {
  const n = Number(value)
  emitRow({ capacity: Number.isFinite(n) && n > 0 ? Math.round(n) : null })
}

function toggleDay(code: string) {
  const days = weekdays.value.includes(code)
    ? weekdays.value.filter((d) => d !== code)
    : [...weekdays.value, code]
  emitRow({ rrule: weeklyRrule(days) })
}

/** A new kind keeps the row's day and fills what the kind needs. */
function setKind(kind: TimeKind) {
  const p = parts.value
  const extra: Partial<TimeRow> = {
    rrule:
      kind === "hours" ? (props.row.rrule ?? weeklyRrule(["MO", "TU", "WE", "TH", "FR"])) : null,
    capacity: kind === "slot" ? (props.row.capacity ?? 4) : null,
  }
  if (kind === "instant") return patchParts({ kind, endDate: null, endTime: null }, extra)
  if (kind === "event") {
    return patchParts(
      { kind, endDate: p.endDate ?? p.date, endTime: p.allDay ? null : (p.endTime ?? p.time) },
      extra
    )
  }
  if (kind === "lifetime")
    return patchParts({ kind, allDay: true, time: null, endTime: null }, extra)
  // Hours and slots are timed windows on a day.
  const time = p.time ?? (kind === "hours" ? "09:00" : "14:00")
  const endTime =
    p.endTime && p.endTime !== p.time ? p.endTime : kind === "hours" ? "17:00" : "15:00"
  return patchParts({ kind, allDay: false, time, endTime, endDate: p.date }, extra)
}

// --- Typing it ------------------------------------------------------------------

/** What's in the typed box while someone types; null shows the row's own form. */
const typedText = ref<string | null>(null)
const typable = computed(() => parts.value.kind === "instant" || parts.value.kind === "event")
const typed = computed(
  () => typedText.value ?? (given.value && parts.value.date ? typedForm(parts.value) : "")
)
const typedError = computed(() => {
  if (typedText.value == null) return null
  const result = parseWhen(typedText.value, {
    base: parts.value.date || null,
    ranges: parts.value.kind !== "instant",
  })
  return "error" in result ? result.error : null
})

function onTyped(text: string) {
  const result = parseWhen(text, {
    base: parts.value.date || null,
    ranges: parts.value.kind !== "instant",
  })
  if (isParsed(result)) {
    // A run of days or a time range makes an instant an event.
    const kind: TimeKind =
      parts.value.kind === "instant" &&
      (result.endTime || (result.endDate && result.endDate !== result.date))
        ? "event"
        : parts.value.kind
    patchParts({
      kind,
      date: result.date,
      time: result.time ?? null,
      endDate: kind === "instant" ? null : (result.endDate ?? result.date),
      endTime: result.endTime ?? (kind === "instant" ? null : (result.time ?? null)),
      allDay: result.allDay,
    })
  }
  typedText.value = text
}

const kindIcon = computed(() => TIME_KINDS.find((k) => k.key === parts.value.kind)?.icon ?? "clock")
const summaryText = computed(() => {
  const shown = formatTime(props.row)
  return `${shown.text}${shown.zone ? ` ${shown.zone}` : ""}`
})
const hint = computed(() => typedError.value ?? (given.value ? summary.value : ""))

const summary = computed(() => {
  const p = parts.value
  if (!p.date) return ""
  const shown = formatTime(props.row)
  return `${longDay(p.date).split(",")[0]}, ${shown.text}${shown.zone ? ` ${shown.zone}` : ""}`
})

// --- Zones ----------------------------------------------------------------------

const COMMON_ZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "Europe/Madrid",
  "UTC",
]
const zones = computed(() => [...new Set([parts.value.tz, getBrowserTimeZone(), ...COMMON_ZONES])])
const zoneName = (tz: string) =>
  `${tz.split("/").pop()?.replace(/_/g, " ")} (${zoneAbbreviation(tz)})`

// --- The line -------------------------------------------------------------------

/** A click on the line itself, not its input or a button, opens or closes it. */
function onHeadClick(event: MouseEvent) {
  if ((event.target as HTMLElement).closest("input, button, select, a")) return
  emit("toggle")
}

const quickRef = ref<HTMLInputElement | null>(null)
onMounted(() => {
  if (props.seed) onTyped(props.seed)
  if (!props.autofocus) return
  nextTick(() => {
    const input = quickRef.value
    if (!input) return
    input.focus()
    // A seeded key is the start of what's typed; an existing time is replaced.
    if (props.seed) input.setSelectionRange(input.value.length, input.value.length)
    else input.select()
  })
})

defineExpose({
  focus: () => quickRef.value?.focus(),
  /** Whether the line holds typed text (read or not). */
  hasText: () => !!quickRef.value?.value.trim(),
})
</script>

<style>
/* The line, its fields panel, and their boxes are RowsEditor's; these are
   the inputs particular to a time. */
.time-row-editor {
  display: flex;
  flex-direction: column;

  .day {
    width: 2.2em;
    padding: 0;

    &.is-selected {
      background: var(--auger-primary);
      border-color: var(--auger-primary);
      color: var(--auger-text-inverted);
    }
  }

  .date {
    width: 9.5em;
  }

  .clock {
    width: 7.5em;
  }

  .seats {
    width: 4.5em;
  }

  .zone {
    width: auto;
  }

  .title {
    flex: 1 1 8em;
    min-width: 0;
  }

  .to {
    color: var(--auger-text-weak);
  }
}
</style>
